/**
 * PreviewGalleryView — pure presentational component.
 *
 * Renders:
 *  - Case header: friendly short studyId, optional meta, non-diagnostic disclaimer, "Open report" action
 *  - Findings region: CSS auto-fill grid, vertically scrollable inside the stage (page never scrolls)
 *  - One FindingPreviewTile per finding — ALL findings rendered, no cap
 *
 * No measurement, no network, no Cornerstone — all that lives in PreviewGallery.
 */

import React from "react";
import { FindingPreviewTile } from "./FindingPreviewTile";
import { Button } from "@/design-system/components/react";
import type { GalleryFinding } from "../hooks/useStudyQueries";

export interface TileState {
  imageUrl: string | null;
  loading: boolean;
}

interface PreviewGalleryViewProps {
  findings: GalleryFinding[];
  /** Per-condition { imageUrl, loading } — replaces the old frameByCondition map. */
  tileStateByCondition: Record<string, TileState>;
  studyId: string;
  meta?: string;
  onOpenReport: () => void;
  /** Optional: called when a tile is clicked to open the expanded modal. */
  onSelectFinding?: (finding: GalleryFinding) => void;
}

export const PreviewGalleryView: React.FC<PreviewGalleryViewProps> = ({
  findings,
  tileStateByCondition,
  studyId,
  meta,
  onOpenReport,
  onSelectFinding,
}) => {
  // Display the friendly short id: last 8 chars of the first :: segment.
  // Full studyId is kept for React keys and bbox fetch — only the display changes.
  const displayId = studyId.split("::")[0]?.slice(-8) ?? studyId;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Case header */}
      <div className="flex flex-none flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-signal-text">{displayId}</span>
        {meta && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-fg-dim">
            {meta}
          </span>
        )}
        {/* Non-diagnostic disclaimer — not a compliance badge, purely functional copy */}
        <span className="rounded-sharp border border-border px-1.5 py-0.5 font-mono text-[9px] text-fg-faint">
          Informational preview · not for primary diagnosis
        </span>
        <div className="ml-auto">
          <Button size="sm" variant="default" className="rounded-sharp" onClick={onOpenReport}>
            Open report →
          </Button>
        </div>
      </div>

      {/* Findings region — scrolls vertically inside the stage; page never scrolls */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          {findings.map((finding) => {
            const tile = tileStateByCondition[finding.condition];
            return (
              <div
                key={finding.condition}
                data-preview-tile
                onClick={onSelectFinding ? () => onSelectFinding(finding) : undefined}
                className={
                  onSelectFinding
                    ? "cursor-pointer rounded-sharp ring-offset-bg transition-shadow hover:ring-2 hover:ring-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal"
                    : undefined
                }
                role={onSelectFinding ? "button" : undefined}
                tabIndex={onSelectFinding ? 0 : undefined}
                onKeyDown={
                  onSelectFinding
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") onSelectFinding(finding);
                      }
                    : undefined
                }
                aria-label={onSelectFinding ? `Expand preview for ${finding.condition}` : undefined}
              >
                <FindingPreviewTile
                  condition={finding.condition}
                  category={finding.category}
                  imageUrl={tile?.imageUrl ?? null}
                  loading={tile?.loading ?? true}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* DEV-only diagnostic hint when nothing rendered — points at the console
          shape diagnostics from validateGalleryShape. Prod shows the plain
          (empty) grid, never these internals. */}
      {findings.length === 0 && import.meta.env.DEV && (
        <p className="font-mono text-[10px] text-fg-faint">
          No previews — see console for shape diagnostics
        </p>
      )}
    </div>
  );
};
